// frontend\src\pages\user\Profile.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../../style/user/Profile.css";
import { useAuth } from "../../context/AuthContext";

export default function Profile({ logout, onMenuClick, onClose }) {
  const { currentUser, updateUser } = useAuth(); // Tambahkan updateUser dari context
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  // Deteksi ukuran layar
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cegah scroll background
  useEffect(() => {
    if (showLogoutModal || (isMobile && !showLogoutModal)) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, showLogoutModal]);

  // Event listener untuk profile update dari halaman edit
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      console.log("Profile update detected in Profile component");
      // Trigger re-render dengan memanggil updateUser jika ada
      if (event.detail && updateUser) {
        updateUser(event.detail);
      }
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
  }, [updateUser]);

  const handleEditProfile = (e) => {
    e.stopPropagation(); // Mencegah event bubbling
    e.preventDefault();
    
    // Navigasi ke halaman edit profil
    navigate("/edit-profil");
    
    // Panggil onMenuClick untuk menutup dropdown
    if (onMenuClick) {
      onMenuClick();
    }
  };

  const handleLogoutClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Proses logout
    logout();
    setShowLogoutModal(false);
    
    // Panggil onClose untuk menutup dropdown
    if (onClose) {
      onClose();
    }
  };

  const handleCancelLogout = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowLogoutModal(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('profile-overlay')) {
      if (onClose) onClose();
    }
  };

  const handleModalContentClick = (e) => {
    e.stopPropagation();
  };

  const handleModalOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCancelLogout(e);
    }
  };

  // Handler untuk klik di area profile-card (mencegah penutupan dropdown)
  const handleProfileCardClick = (e) => {
    e.stopPropagation();
  };

  return (
    <>
      {/* Overlay untuk mobile */}
      {isMobile && !showLogoutModal && (
        <div 
          className="profile-overlay" 
          onClick={handleOverlayClick}
        />
      )}
      
      {/* Profile Card - dengan stopPropagation */}
      <div 
        className={`profile-dropdown-container ${showLogoutModal ? 'modal-open' : ''}`}
        onClick={handleProfileCardClick}
      >
        <div className="profile-card">
          <div className="profile-header">
            <h2 className="profile-title">Profile</h2>
            <button 
              className="profile-close-btn" 
              onClick={(e) => {
                e.stopPropagation();
                if (onClose) onClose();
              }}
              aria-label="Close profile"
            >
              ×
            </button>
          </div>

          <div className="profile-info">
            {currentUser?.profile_image ? (
              <img
                src={currentUser.profile_image}
                alt="Profile"
                className="profile-avatar"
                key={currentUser.profile_image} // Tambahkan key untuk force re-render
                onError={(e) => {
                  console.error("Profile avatar error:", e.target.src);
                  e.target.style.display = "none";
                  // Tampilkan fallback
                  e.target.nextElementSibling?.style.removeProperty('display');
                }}
              />
            ) : null}
            {/* Fallback avatar - selalu ada tapi hidden jika image ada */}
            <div 
              className="profile-fallback-large" 
              style={{ display: currentUser?.profile_image ? 'none' : 'flex' }}
            >
              {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : "U"}
            </div>
            <div className="profile-details">
              <p className="profile-name">{currentUser?.name || "User"}</p>
              <p className="profile-email">{currentUser?.email || "email@example.com"}</p>
            </div>
          </div>

          <div className="profile-menu">
            <div 
              className="profile-action" 
              onClick={handleEditProfile}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleEditProfile(e);
                }
              }}
            >
              <span className="profile-icon">👤</span>
              <span>Edit Profile</span>
            </div>

            <div 
              className="logout-button" 
              onClick={handleLogoutClick}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleLogoutClick(e);
                }
              }}
            >
              <span className="logout-icon">↪</span>
              <span>Log Out</span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Logout */}
      {showLogoutModal && (
        <div className="modal-overlay" onClick={handleModalOverlayClick}>
          <div className="modal" onClick={handleModalContentClick}>
            <p className="modal-title">Are you sure want to log out?</p>
            <p className="modal-text">
              You will be logged out of your account and may need to log in again to continue using the services.
            </p>
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={handleCancelLogout}
                type="button"
              >
                Cancel
              </button>
              <button 
                className="btn-confirm" 
                onClick={handleLogoutConfirm}
                type="button"
              >
                Yes, Sure
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}